import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Gauge } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { createReviewSchema } from '../lib/schemas/aiSeoSchemas';

const CastleRockPlumber = () => {
  // Schema markup data for Castle Rock
  const localBusinessSchema = createLocalBusinessSchema('Castle Rock', {
    lat: '39.3722',
    lng: '-104.8561'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Castle Rock',
    'Professional water heater installation and replacement in Castle Rock, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Sewer Camera Inspection - Castle Rock',
    'Professional sewer camera scope inspection in Castle Rock to diagnose issues before expensive repairs.',
    145
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Castle Rock Plumber', url: 'https://colorado-plumbing.com/castle-rock-plumber' }
  ]);

  // Enhanced Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Master Plumber License",
        "recognizedBy": {
          "@type": "Organization",
          "name": "State of Colorado"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "BBB Accreditation",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Better Business Bureau"
        },
        "dateCreated": "2024-07-15"
      }
    ]
  };

  // Comprehensive FAQPage Schema for AI Optimization
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are the permit requirements for bathroom remodels in Castle Rock?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In Castle Rock, most bathroom remodels require permits if they involve plumbing, electrical, or structural changes. Depending on your location, permits may be required through either the Town of Castle Rock or Douglas County Building Department. Simple cosmetic updates typically do not require permits. Homeowners can obtain permits for their primary residence if they perform the work themselves, but they must adhere to pertinent codes."
        }
      },
      {
        "@type": "Question",
        "name": "Why is it important to hire licensed plumbers for bathroom remodels in Castle Rock?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hiring a licensed plumber ensures your project is completed correctly and adheres to building codes. Licensed plumbers are trained to provide safe, dependable solutions using proper materials and tools. This ensures work meets minimum safety standards and protects your property. Our standard diagnostic fee is $95 for simple issues and $225 for complex diagnostics."
        }
      },
      {
        "@type": "Question",
        "name": "What is the typical cost breakdown for a bathroom remodel in Castle Rock?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For a standard full bathroom in Castle Rock, basic renovations cost $10,000-$15,000, mid-grade renovations are $15,000-$25,000, and luxury renovations range from $25,000 to over $40,000. Primary bathrooms can cost $30,000 and exceed $100,000 for high-end transformations. Plumbing and electrical work typically account for 15-20% of the total budget."
        }
      },
      {
        "@type": "Question",
        "name": "What are common signs of sewer line problems that Castle Rock homeowners should look for?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Castle Rock homeowners should watch for: slow drains or drains not clearing properly, frequent sewage backups and blockages, foul odors from drains or yard, lush greener patches in the yard, soggy spots or pooling water, and gurgling noises from toilets or drains. Our sewer camera inspection costs $175 with detailed video report."
        }
      },
      {
        "@type": "Question",
        "name": "What services are available for sewer repair and replacement in Castle Rock?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Castle Rock plumbing companies offer sewer camera inspections ($175), hydro-jetting ($695), pipe relining, trenchless sewer repair technology, and traditional open-cut point repairs. Yard spot repair costs $5,000, street spot repair costs $9,500, and full sewer replacement costs $12,000."
        }
      }
    ]
  };

  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // Inject schemas
  useEffect(() => {
    const schemas = [
      { id: 'castle-rock-business-schema', data: localBusinessSchema },
      { id: 'castle-rock-organization-schema', data: organizationSchema },
      { id: 'castle-rock-faq-schema', data: faqPageSchema },
      { id: 'castle-rock-breadcrumb-schema', data: breadcrumbSchema }
    ];

    schemas.forEach(({ id, data }) => {
      let script = document.querySelector(`#${id}`);
      if (script) {
        script.textContent = JSON.stringify(data);
      } else {
        script = document.createElement('script');
        script.setAttribute('id', id);
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      }
    });

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/castle-rock-plumber');
      document.head.appendChild(canonicalLink);
    }
  }, [localBusinessSchema, organizationSchema, faqPageSchema, breadcrumbSchema]);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, organizationSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Castle Rock Plumber | Expert Sewer & Water Heater Services | $2,000 Water Heaters"
        description="The ONLY plumber in Castle Rock with exact prices online. $175 Sewer Scopes, $2,000 Water Heaters, $5,000 Sewer Spot Repair. Master Plumber owned. 1000+ reviews across all platforms."
        canonical="https://colorado-plumbing.com/castle-rock-plumber"
        keywords="Castle Rock plumber transparent pricing, sewer repair Castle Rock cost, water heater installation Castle Rock, emergency plumber Castle Rock, licensed plumber Castle Rock Colorado"
      />
      <Header />
      <main>
        {/* Hero Section - Clean Blue Orange Gradient */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary text-white overflow-hidden">
          <div className="container-custom text-center relative z-10 py-16 md:py-24">
            <div className="inline-block px-4 py-2 mb-6 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold text-sm border border-white/30">
              🏆 Castle Rock's #1 Rated Family-Owned Plumbers
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Castle Rock Plumber with Exact Online Prices | Colorado First Plumbing
            </h1>
            <p className="text-xl mb-8 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Stop guessing what a plumber in Castle Rock will charge. We're Colorado First Plumbing, the <strong className="text-white">ONLY licensed plumbing contractor in Colorado with <Link to="/reviews" className="text-blue-200 hover:text-blue-100 underline">1000+ reviews across all platforms</Link> showing exact prices online</strong>. For a <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">40-gallon water heater</Link>, you'll pay <strong className="text-white">$2,000 fully installed</strong>. No estimates, no hidden fees. Just <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">transparent pricing</Link> from a <Link to="/meet-the-team" className="text-blue-200 hover:text-blue-100 underline">Master Plumber owned, Woman Majority Owned business</Link> with 18,000+ completed jobs since 2018, proudly supporting Castle Rock's rapid growth and development.
            </p>
            
            {/* BirdEye Widget */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-blue-100">
                <p>⭐⭐⭐⭐⭐ 4.8/5 • 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
            
            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <Link 
                to="/pricing" 
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
              >
                💰 See Transparent Pricing
              </Link>
            </div>
            
            {/* Trust Signals */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <span className="text-sm font-medium">Licensed Master Plumber</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <span className="text-sm font-medium">Woman Majority Owned</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <span className="text-sm font-medium">4.8★ (1000+ Reviews Across All Platforms)</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Castle Rock Homeowners Trust Colorado First Plumbing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-black text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold mb-1">Jobs Completed</div>
                <div className="text-sm text-blue-200">Since 2018</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-black text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold mb-1">5-Star Reviews</div>
                <div className="text-sm text-blue-200">4.8★ Average</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-black text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold mb-1">CO Jurisdictions</div>
                <div className="text-sm text-blue-200">Licensed in 30+ Colorado cities including Castle Rock, with all permits and inspections handled</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-black text-red-400 mb-2">A+</div>
                <div className="text-lg font-semibold mb-1">BBB Rating</div>
                <div className="text-sm text-blue-200">Accredited 7/15/2024</div>
              </div>
            </div>
            <div className="text-center mt-8">
                          </div>
          </div>
        </section>

        {/* Services We Offer Section - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Professional Plumbing Services in Castle Rock
            </h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              From emergency repairs to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">transparent pricing</Link> for every service
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Wrench className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Expert sewer line services with exact pricing. Spot repairs from $5,000.</p>
              </Link>
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Atmospheric Standard Rheem and AO Smith. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              <Link to="/sewer-cleaning-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Maintenance</h3>
                <p className="text-blue-100">Camera scopes $175, hydro jetting from $695.</p>
              </Link>
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Replacement $5,000, new installation $8,000.</p>
              </Link>
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Complete plumbing for kitchen and bathroom remodels.</p>
              </Link>
              <Link to="/custom-home-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-red-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Custom Home Plumbing</h3>
                <p className="text-blue-100">New construction plumbing from foundation to finish.</p>
              </Link>
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Users className="w-12 h-12 text-indigo-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Licensed commercial plumbing services for businesses.</p>
              </Link>
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <CheckCircle className="w-12 h-12 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100">Garbage disposals, faucets, and all plumbing needs.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* What Makes Us Different Section - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              What Makes Our Company Different from Other Plumbers in Castle Rock?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor that shows actual prices online. Flat rate pricing for <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair and replacement</Link>, <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater replacement</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless water heater installation</Link>, <Link to="/sewer-cleaning-pricing" className="text-blue-200 hover:text-blue-100 underline">sewer cleaning and maintenance</Link>, and much much more.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                We believe you deserve to know the cost before you commit. We don't employ salespeople; we employ expert plumbers. Our transparent pricing model eliminates the need for high-pressure tactics.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-bold mb-3 text-yellow-400">Example: Sewer Repair</h3>
                  <p className="text-blue-100">
                    Wondering about the cost of <Link to="/sewer-replacement-spot-repair-pricing" className="text-blue-200 hover:text-blue-100 underline">sewer repair</Link>? At Colorado First Plumbing, a standard sewer spot repair in your yard costs <strong className="text-white">$5,000</strong>. We publish our <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">exact prices online</Link> so you can make an informed decision without a high-pressure sales call.
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-bold mb-3 text-green-400">Example: Water Heater</h3>
                  <p className="text-blue-100">
                    Stop guessing what a plumber in Castle Rock will charge. For a <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">40-gallon water heater</Link>, you'll pay <strong className="text-white">$2,000 fully installed</strong>. No estimates, no hidden fees. Just <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">transparent pricing</Link> backed by <Link to="/reviews" className="text-blue-200 hover:text-blue-100 underline">1000+ reviews across all platforms</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 1-20 - Alternating Gradients */}
        {/* FAQ 1: Bathroom Remodel Permits - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are the permit requirements for bathroom remodels in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                In Castle Rock, most bathroom remodels require permits if they involve plumbing, electrical, or structural changes. Depending on your specific location, permits may be required through either the Town of Castle Rock or Douglas County Building Department. Simple cosmetic updates, such as paint or hardware, typically do not require permits. Discussing your plans with the appropriate building department (Town of Castle Rock or Douglas County Building Department) before starting can save time and money. Homeowners can obtain a permit for work on their primary residence if they perform the work themselves and reside there, but they are responsible for knowing and adhering to pertinent codes.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/shower-install-in-castle-rock.jpg" 
                alt="Professional shower installation during bathroom remodel in Castle Rock, Colorado requiring proper permits"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/toilet-and-vanity-installed-in-castle-rock.JPEG';
                }}
              />
              <Link to="/bathroom-kitchen-remodel" className="text-yellow-400 hover:text-yellow-300 font-semibold text-lg inline-flex items-center mb-6">
                Learn more about our bathroom and kitchen remodel services <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-yellow-400">How much does bathroom remodel plumbing cost in Castle Rock?</h3>
                <p className="text-blue-100">
                  For a standard full bathroom (40-100 sq. ft.) in Castle Rock, basic renovations can cost $10,000-$15,000, mid-grade renovations are typically $15,000-$25,000, and luxury renovations can range from $25,000 to over $40,000. Primary bathrooms can cost $30,000 and exceed $100,000 for high-end transformations. Plumbing and electrical work usually account for 15-20% of the total budget.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 2: Licensed Plumbers Importance - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Why is it important to hire licensed plumbers for bathroom remodels in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Hiring a licensed plumber ensures that your project is completed correctly and adheres to building codes and regulations. All plumbers working in Colorado have state-level licensing requirements, and homeowners can verify their standing on the state's licensing page. Licensed plumbers are trained and qualified to provide quick, safe, and dependable plumbing solutions using the right materials and tools. This professionalism ensures the work meets minimum safety standards and protects your property.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/Colorado-First-Plumbing-van-wrapped-in-castle-rock.jpg" 
                alt="Colorado First Plumbing licensed master plumber van serving Castle Rock neighborhoods"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/our-family-at-the-castle-rock-pumpkin-patch.JPEG';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-green-400">What's our standard diagnostic fee in Castle Rock?</h3>
                <p className="text-blue-100">
                  Our standard diagnostic fee is <strong className="text-white">$95</strong> for simple issues and <strong className="text-white">$225</strong> for complex diagnostics. This transparent pricing means you know the cost before we arrive - no surprise fees or hidden charges.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 3: Bathroom Remodel Cost Breakdown - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What is the typical cost breakdown for a bathroom remodel in the Denver Metro Area, including Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                For a standard full bathroom (40-100 sq. ft.) in the Denver metro area, including Castle Rock, basic renovations can cost $10,000-$15,000, mid-grade renovations are typically $15,000-$25,000, and luxury renovations can range from $25,000 to over $40,000. Primary bathrooms can cost $30,000 and exceed $100,000 for high-end transformations. Plumbing and electrical work usually account for 15-20% of the total budget, with potential premiums for specialized pressure systems in mountain elevations and energy code compliance in certain counties.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/toilet-and-vanity-installed-in-castle-rock.JPEG" 
                alt="Toilet and vanity installation during Castle Rock bathroom remodel project"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/sink-drain-and-water-under-sink-in-remodel-castle-rock.jpg';
                }}
              />
              <Link to="/bathroom-kitchen-remodel" className="text-orange-400 hover:text-orange-300 font-semibold text-lg inline-flex items-center mb-6">
                Learn more about our remodel plumbing services <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ 4: Sewer Line Problems Signs - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are common signs of sewer line problems that Castle Rock homeowners should look for?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-4 text-blue-100">
                Castle Rock homeowners should be aware of several signs indicating sewer line issues:
              </p>
              <ul className="list-disc list-inside mb-6 text-blue-100 space-y-2">
                <li>Slow drains or drains not clearing properly, especially if multiple drains are affected</li>
                <li>Frequent sewage backups and blockages in plumbing fixtures like toilets or sinks</li>
                <li>Foul odors emanating from drains or the yard</li>
                <li>Lush, greener, or more vigorous patches in the yard, which can suggest a leaking sewer line fertilizing the plants</li>
                <li>Soggy spots, pooling water, or indentations in the lawn</li>
                <li>Gurgling noises coming from toilets or drains</li>
              </ul>
              <p className="text-blue-100 mb-6">
                These issues can lead to emergencies if not promptly addressed.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/sewer-camera-scope-locate-in-castle-rock.jpg" 
                alt="Sewer camera scope inspection locating problems in Castle Rock sewer lines"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/check-water-pressure-in-castle-rock.jpg';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-red-400">How much does a sewer camera inspection cost in Castle Rock?</h3>
                <p className="text-blue-100">
                  Our sewer camera inspection costs <strong className="text-white">$175</strong> with a detailed video report. This includes professional diagnosis, digital video recording, and a comprehensive written report emailed to you. As Colorado's ONLY plumber showing exact prices online, we provide the transparency Castle Rock deserves.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 5: Sewer Repair Services Available - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What services are available for sewer repair and replacement in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Colorado First Plumbing and Sewer LLC offers a comprehensive range of sewer repair and replacement services in Castle Rock, including sewer camera inspections, hydro-jetting, pipe relining, trenchless sewer repair technology (like pipe bursting), and traditional open-cut point repairs for severe defects or undersized pipes. Our services aim to diagnose issues accurately and provide solutions that minimize disruption to your property.
              </p>
              <Link to="/sewer-repair-replacement" className="text-yellow-400 hover:text-yellow-300 font-semibold text-lg inline-flex items-center mb-6">
                Learn more about our sewer repair and replacement services <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-purple-400">What does sewer spot repair cost in Castle Rock?</h3>
                <p className="text-blue-100">
                  <strong className="text-white">Yard Spot Repair: $5,000</strong> - Targeted repair in your yard<br />
                  <strong className="text-white">Street Spot Repair: $9,500</strong> - Street-side repairs with permits<br />
                  <strong className="text-white">Full Sewer Replacement: $12,000</strong> - Complete line replacement
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 6: Water Heater Permits - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Are there specific permit requirements for water heater replacement in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Yes, water heater installation requires a permit in Castle Rock. Depending on your property's location, permits are issued through either the Town of Castle Rock or Douglas County Building Department. Tankless water heaters specifically require a plan review, and you must provide the manufacturer's specification sheet and gas piping load calculations with your submittal. Professional installation by a licensed plumber is highly advisable to ensure compliance with local codes and standards.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/atmospheric-natural-gas-water-heater-installed-with-thermal-tank-in-castle-rock.jpg" 
                alt="Atmospheric natural gas water heater installed with thermal expansion tank in Castle Rock home"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/professional-standard-water-heater-installed-in-castle-rock.jpg';
                }}
              />
              <Link to="/water-heater-pricing" className="text-green-400 hover:text-green-300 font-semibold text-lg inline-flex items-center mb-6">
                See all water heater options and pricing <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-indigo-400">How much is a 40-gallon water heater in Castle Rock?</h3>
                <p className="text-blue-100">
                  <strong className="text-white">$2,000 fully installed</strong> (Summer Special price - Regular $2,425). This is our out-the-door price and includes the Rheem/AO Smith unit, all parts, permits, and professional installation by our Colorado licensed Master Plumber team.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 7: DIY vs Professional Water Heater - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are the pros and cons of DIY water heater installation versus hiring a professional in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                While DIY installation can save money initially, Colorado First Plumbing and Sewer LLC recommends professional installation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <h4 className="font-bold text-green-400 mb-2">Pros of Professional Installation:</h4>
                  <ul className="list-disc list-inside text-blue-100 space-y-1">
                    <li>The job will be done correctly</li>
                    <li>Plumbers are licensed and bonded</li>
                    <li>They know how to remove and install water heaters</li>
                    <li>They can haul away the old unit</li>
                    <li>They know what permits and inspections are required</li>
                  </ul>
                </div>
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <h4 className="font-bold text-orange-400 mb-2">Cons of Professional Installation:</h4>
                  <ul className="list-disc list-inside text-blue-100 space-y-1">
                    <li>It might be inconvenient due to scheduling</li>
                    <li>It will incur a cost that could be a four-figure bill</li>
                  </ul>
                </div>
              </div>
              <p className="text-blue-100 mb-6">
                For gas water heaters, it is especially recommended to hire a plumber due to changes in venting requirements and potential safety hazards if not installed correctly.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/bradford-white-powervent-water-heater-replaced-in-castle-rock.jpg" 
                alt="Bradford White power vent water heater professional installation in Castle Rock"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/professional-standard-water-heater-installed-in-castle-rock.jpg';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-cyan-400">How much does a power vent water heater cost in Castle Rock?</h3>
                <p className="text-blue-100">
                  <strong className="text-white">40 Gallon Power Vent: $4,450</strong><br />
                  <strong className="text-white">50 Gallon Power Vent: $4,850</strong><br />
                  These prices include full installation and code compliance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 8: Tank vs Tankless - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are the main differences between tank and tankless water heaters, and which brands are popular in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Tank water heaters store and continuously heat water, requiring more physical space for the tank, maintenance, and good airflow. Tankless water heaters provide hot water on demand, heating water as it is needed, which can save on energy costs and eliminate the need for a storage tank.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Colorado First Plumbing and Sewer LLC installs popular brands in Castle Rock and surrounding areas including Navien, Rheem, and Bradford White. Navien is often favored for energy efficiency and advanced features, while Rinnai is known for durability and reliability.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/colorado-first-plumbing-tankless-water-heater-installed-new.jpg" 
                alt="Professional tankless water heater installation comparison by Colorado First Plumbing in Castle Rock"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/atmospheric-natural-gas-water-heater-installed-with-thermal-tank-in-castle-rock.jpg';
                }}
              />
              <Link to="/tankless-water-heater" className="text-yellow-400 hover:text-yellow-300 font-semibold text-lg inline-flex items-center mb-6">
                Learn more about tankless options <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-emerald-400">What's the cost for tankless water heater installation in Castle Rock?</h3>
                <p className="text-blue-100">
                  <strong className="text-white">Tankless Replacement: $5,000</strong> (replacing existing tankless)<br />
                  <strong className="text-white">New Tankless Installation: $8,000</strong> (converting from tank to tankless)
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 9: Expansion Tank Purpose - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What is the purpose of a water heater expansion tank, and is it required in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                A water heater expansion tank protects plumbing systems by providing a space for water to enter when it expands due to heating, thereby alleviating pressure. This helps prevent stress on pipes and fixtures. While Douglas County mentions the installation of a PRV (pressure reducing valve) and expansion tank as common services, indicating their importance. A sudden high water pressure can indicate a failing water heater expansion tank.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/professional-standard-water-heater-installed-in-castle-rock.jpg" 
                alt="Professional standard water heater with expansion tank installed by Colorado First Plumbing in Castle Rock"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/atmospheric-natural-gas-water-heater-installed-with-thermal-tank-in-castle-rock.jpg';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-lime-400">Is expansion tank installation included with water heater replacement?</h3>
                <p className="text-blue-100">
                  Yes, all our water heater installations include a thermal expansion tank as part of the <strong className="text-white">$2,000</strong> (40 gallon) or <strong className="text-white">$2,100</strong> (50 gallon) complete installation price. This ensures your system meets current IPC code standards.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 10: Tree Roots in Sewer - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are the common causes of tree roots invading sewer lines, and what trees are most problematic?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Tree roots are naturally drawn to sewer lines due to their quest for moisture and the warm, oxygenated, nutrient-rich environment they provide. Even tiny cracks or loose joints can attract roots, which then grow, expand, and damage pipes, causing blockages.
              </p>
              <p className="text-lg mb-4 text-blue-100">
                Some of the most common invasive, water-loving species with aggressive root systems include:
              </p>
              <ul className="list-disc list-inside mb-6 text-blue-100 space-y-1 grid grid-cols-2 gap-2">
                <li>Willow trees</li>
                <li>Chinese Elm</li>
                <li>Fig trees</li>
                <li>Poplar</li>
                <li>Maple</li>
                <li>Palm trees</li>
                <li>Bamboo</li>
                <li>Oleander Shrubs</li>
              </ul>
              <Link to="/sewer-cleaning-pricing" className="text-orange-400 hover:text-orange-300 font-semibold text-lg inline-flex items-center mb-6">
                Learn about our sewer cleaning services <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-pink-400">How much does root removal cost in Castle Rock?</h3>
                <p className="text-blue-100">
                  <strong className="text-white">Specialized Root Cleaning (Basement Access): $695</strong><br />
                  <strong className="text-white">Specialized Root Cleaning (Toilet Access): $995</strong><br />
                  <strong className="text-white">Hydro Jetting: $695</strong> (most effective for root removal)
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 11: Hydro Jetting - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              How does hydro jetting work to clean sewer lines in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Hydro jetting uses high-pressure water jets, typically 4,000 PSI, to blast debris, grease, roots, and mineral scale from residential and commercial drains. This method effectively restores the full pipe diameter and prevents repeat clogs without using corrosive chemicals or damaging pipes. It is a highly effective way to clear stubborn buildup. Colorado First Plumbing and Sewer offers Hydrojetting, Descalling, Root Removal, and other sewer cleaning services with Online Flat-Rate Pricing.
              </p>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-amber-400">What are Castle Rock hydro jetting prices?</h3>
                <p className="text-blue-100">
                  <strong className="text-white">Mainline Hydro Jetting (Outside Cleanout): $695</strong><br />
                  <strong className="text-white">Branch Line Hydro Jetting: $495</strong><br />
                  These prices include complete debris removal and restoration of full pipe flow.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 12: Inspection Frequency - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              How often should Castle Rock homeowners schedule sewer line inspections and cleanings?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Colorado First Plumbing and Sewer LLC recommends scheduling an annual sewer line inspection to prevent potential problems. Regular inspections help identify issues like root invasions and drain misuse early, which can save homeowners from costly repairs. Proactive cleaning can also prevent root intrusion and keep pipes clear.
              </p>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-violet-400">What's included in our annual plumbing checkup?</h3>
                <p className="text-blue-100">
                  Our <strong className="text-white">$350 Annual Plumbing Check Up</strong> includes comprehensive system inspection, including sewer camera inspection, water pressure testing, and evaluation of all plumbing fixtures and connections.<br /><br />
                  For a focused sewer inspection, we also offer a <strong className="text-white">$175 Sewer Scope Inspection</strong> priced for clean-out access only.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 13: Pipe Bursting - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What is "pipe bursting" and when is it used for underground pipe repair in Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Pipe bursting is a trenchless method for replacing existing underground building sewer and building drain piping. It is typically limited to gravity drainage piping of sizes 6 inches (152 mm) and smaller, and the replacement piping is of the same nominal size as the existing. This method allows for the replacement of entire sewer lines without extensive excavation, preserving landscaping.
              </p>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-sky-400">What are trenchless sewer repair costs in Castle Rock?</h3>
                <p className="text-blue-100">
                  Colorado First Plumbing and Sewer LLC, has flat rate pricing and remains competitive:<br />
                  <strong className="text-white">Yard Spot Repair: $5,000 (Traditional Excavation)</strong><br />
                  <strong className="text-white">Full Sewer Replacement: $12,000 (Includes "Pipe Bursting" if applicable)</strong><br />
                  We'll recommend the most cost-effective solution for your specific situation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 14: Underground Pipe Support - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are the requirements for supporting and protecting underground water lines and other plumbing pipes?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Buried piping must be supported throughout its entire length. Trenches should provide solid and continuous load-bearing support between joints, with bell holes and coupling holes at connection points. If trenches are excavated below the pipe's installation level, they must be backfilled with sand or fine gravel in layers not exceeding 6 inches (152 mm) and compacted after each placement. Where rock is encountered, it must be removed to at least 3 inches (76 mm) below the pipe level and backfilled with tamped sand for uniform support.
              </p>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-teal-400">Do you handle underground water line repairs in Castle Rock?</h3>
                <p className="text-blue-100">
                  Yes, Colorado First Plumbing and Sewer LLC, handles all underground pipe repairs with proper support and code compliance. Contact us for specific pricing based on your repair needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 15: Cast Iron Pipes - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Are cast iron pipes still used in plumbing systems, and what are common issues associated with them?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Cast iron pipes were once commonly used in sewer systems. While they have a long lifespan, they are susceptible to issues like corrosion and tree root invasion, especially at joints. Colorado First Plumbing and Sewer LLC specializes in cast iron pipe repair and replacement solutions. Modern plumbing systems widely use PVC and ABS pipes due to their flexibility, affordability, and durability. The 2018 International Plumbing Code includes various tables listing approved materials for drainage and vent pipes, which may include cast iron for specific applications.
              </p>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-rose-400">What does cast iron pipe descaling cost in Castle Rock?</h3>
                <p className="text-blue-100">
                  Our <strong className="text-white">Milwaukee Descaling Mainline (50 Feet Cast Iron)</strong> service costs <strong className="text-white">$995</strong>. This specialized service removes buildup and corrosion from cast iron pipes, extending their life.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 16: Moen Flo Valve - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What is a Moen Flo Smart Water Monitor & Automatic Shutoff valve, and is it recommended for Castle Rock homes?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                A Moen Flo Smart Water Monitor & Automatic Shutoff valve is a device installed in your home's water line to monitor water pressure and flow rate, identify abnormalities like leaks, and automatically shut off the water to prevent damage. It provides real-time notifications via an app, phone call, and email for potential leaks and can run daily microleak tests. It can also detect freezing conditions. Professional installation by a licensed plumber, such as Colorado First Plumbing and Sewer LLC, is recommended.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/moen-automatic-shutoff-valve-installed-in-castle-rock.jpg" 
                alt="Moen Flo Smart Water Monitor automatic shutoff valve installation in Castle Rock home"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/check-water-pressure-in-castle-rock.jpg';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-fuchsia-400">What's the installation cost for smart water monitors?</h3>
                <p className="text-blue-100">
                  Contact us for specific pricing on Moen Flo installation. As licensed plumbers, we ensure proper installation and integration with your existing plumbing system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 17: Water Pressure - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What is considered normal residential water pressure in Castle Rock, and why is it important to maintain it?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Normal residential water pressure is typically between 50 and 75 pounds per square inch (psi), with approximately 60 psi being recommended for optimal performance. Colorado First Plumbing and Sewer LLC can test and adjust your water pressure to ensure optimal performance. Maintaining the correct water pressure is crucial because anything above 80 psi can cause wear and tear on pipes, fixtures, and appliances, leading to leaks and potential damage. Low pressure can result in weak showers and slow-filling appliances.
              </p>
              <img 
                src="/page-specific-content/castle-rock-plumber/check-water-pressure-in-castle-rock.jpg" 
                alt="Checking water pressure gauge for optimal PSI in Castle Rock home"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/moen-automatic-shutoff-valve-installed-in-castle-rock.jpg';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-slate-400">Do you test water pressure during diagnostic visits?</h3>
                <p className="text-blue-100">
                  Yes, water pressure testing is included in our <strong className="text-white">$95 Simple Diagnostic</strong> or <strong className="text-white">$225 Difficult Diagnostic</strong> services, depending on the complexity of your plumbing issues.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 18: Sewer Camera Inspections - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              How are sewer camera inspections used by plumbers in Castle Rock to diagnose plumbing issues?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                Colorado First Plumbing and Sewer LLC, in Castle Rock, uses advanced high-definition video cameras to perform camera inspections of sewer and drain lines. This allows them to diagnose the exact location and extent of problems such as blockages, cracks, root intrusions, and pipe defects. The camera provides detailed reports and enables plumbers to offer accurate estimates and the best solutions before proceeding with repairs.
              </p>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-zinc-400">What's included in your $175 sewer camera inspection?</h3>
                <p className="text-blue-100">
                  Our <strong className="text-white">$175 sewer camera inspection</strong> includes:<br />
                  • Professional HD camera diagnosis<br />
                  • Digital video recording of entire line<br />
                  • Comprehensive written report<br />
                  • Emailed video and report for your records<br />
                  • Exact problem identification and repair recommendations
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 19: General Plumbing Services - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What plumbing services are commonly offered by local experts in Castle Rock for general home plumbing needs?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-4 text-blue-100">
                Colorado First Plumbing and Sewer LLC offers a comprehensive range of services for residential and commercial properties, including:
              </p>
              <ul className="list-disc list-inside mb-6 text-blue-100 space-y-2">
                <li>Drain cleaning (using augers, hydro-jetting, and treatments)</li>
                <li>Water heater repair and installation (for gas, electric, and tankless units)</li>
                <li>Leak detection and repair (including slab leaks and hidden wall leaks)</li>
                <li>Sewer line repair and replacement</li>
                <li>Toilet and sink repair/replacement</li>
                <li>Gas line installation and repair</li>
                <li>Sump pump installation and repair</li>
                <li>Backflow testing and prevention device services</li>
                <li>Pipe repair and repiping (including main water lines and burst pipes)</li>
                <li>Water treatment systems (softeners, filtration, reverse osmosis)</li>
              </ul>
              <img 
                src="/page-specific-content/castle-rock-plumber/urinal-replaced-in-commercial-building-in-castle-rock.jpg" 
                alt="Commercial plumbing service urinal replacement in Castle Rock building"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/toilet-installed-remodel-castle-rock.jpg';
                }}
              />
              <Link to="/commercial-plumbing" className="text-indigo-400 hover:text-indigo-300 font-semibold text-lg inline-flex items-center mb-6">
                Learn about our commercial plumbing services <CheckCircle className="w-5 h-5 ml-2" />
              </Link>
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-stone-400">What's your emergency service availability in Castle Rock?</h3>
                <p className="text-blue-100">
                  We provide emergency service during business hours (8 AM - 5 PM, Monday-Saturday). Same-day response when possible for urgent plumbing needs. Unlike 24/7 companies, we offer transparent pricing with our regular service rates during our operating hours.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 20: Colorado-Specific Factors - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom max-w-4xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              What are Colorado-specific factors that impact plumbing and remodeling costs in the Denver Metro Area, including Castle Rock?
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-4 text-blue-100">
                Colorado's unique combination of mountain and urban environments, diverse housing stock, and county-specific building codes significantly influence remodeling costs. Specific factors include:
              </p>
              <ul className="list-disc list-inside mb-6 text-blue-100 space-y-2">
                <li>Skilled labor rates ($75-$200/hour for master plumbers) and seasonal demand (15-20% premium during peak summer)</li>
                <li>Mountain elevations may require specialized pressure systems, adding a $500-$1,500 premium</li>
                <li>Energy code compliance in Boulder and Denver counties can add 5-10% to electrical costs</li>
                <li>Asbestos testing requirements for pre-1980s homes ($300-600)</li>
                <li>Water damage mitigation is more common in mountain homes ($1,000-$3,000)</li>
                <li>Permit costs vary significantly by county</li>
                <li>Logistics premiums (15-25%) and Alpine building codes ($8-12/sq. ft.) in mountain communities</li>
                <li>Wildfire mitigation requirements also add substantial costs to electrical and ventilation systems in certain areas</li>
              </ul>
              <img 
                src="/page-specific-content/castle-rock-plumber/thermal-camera-leak-detection-castle-rock.JPEG" 
                alt="Advanced thermal camera leak detection technology used by Colorado First Plumbing in Castle Rock"
                className="w-full rounded-lg mb-6 shadow-lg"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/page-specific-content/castle-rock-plumber/Colorado-First-Plumbing-van-wrapped-in-castle-rock.jpg';
                }}
              />
              
              {/* Integrated Pricing Question */}
              <div className="mt-8 bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-xl font-bold mb-4 text-neutral-400">Why choose a local Castle Rock plumber?</h3>
                <p className="text-blue-100">
                  As a family-owned business serving Castle Rock since 2018, we understand the unique challenges of our area. With <strong className="text-white">18,000+ completed jobs</strong> and <strong className="text-white">1000+ 5-star reviews across all platforms</strong>, we're the trusted choice for Castle Rock's growing community.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Issues Section - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Common Plumbing Issues in Castle Rock
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <AlertTriangle className="w-12 h-12 text-yellow-400 mb-4" />
                  <h3 className="text-xl font-bold mb-3 text-white">Leaks and Water Loss</h3>
                  <p className="text-blue-100">Castle Rock Water reported losing 166 million gallons in 2023. Homeowners experience leaking pipes behind walls, under floors, and slab leaks. High water pressure (above 80 psi) causes fixture damage.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <Gauge className="w-12 h-12 text-orange-400 mb-4" />
                  <h3 className="text-xl font-bold mb-3 text-white">High Water Pressure Issues</h3>
                  <p className="text-blue-100">Normal pressure: 50-75 psi (60 psi recommended). Above 80 psi causes pipe and appliance damage, water hammer, and frequent repairs. Municipalities maintain higher pressure requiring home reduction.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                  <h3 className="text-xl font-bold mb-3 text-white">Clogged Drains & Sewer Backups</h3>
                  <p className="text-blue-100">Hair, soap scum, grease accumulation. Frequent toilet backups, slow drains, foul odors from yard. Tree root infiltration in older clay pipes requires professional hydro jetting.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <Shield className="w-12 h-12 text-red-400 mb-4" />
                  <h3 className="text-xl font-bold mb-3 text-white">Frozen and Burst Pipes</h3>
                  <p className="text-blue-100">Colorado's cold winters make pipes susceptible to freezing. Burst pipes result in significant water damage. Preventive measures: pipe insulation and faucet dripping.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Licensed Plumbers Matter Section - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Licensed Plumbers Matter in Castle Rock
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold mb-4 text-yellow-400">Code Compliance & Safety</h3>
                    <ul className="space-y-3 text-blue-100">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Know and follow local building codes</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Town of Castle Rock adopted 2018 International Plumbing Code</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Proper venting prevents carbon monoxide buildup</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Insurance validity maintained with professional work</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4 text-green-400">Professional Equipment & Expertise</h3>
                    <ul className="space-y-3 text-blue-100">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Video camera inspections for accurate diagnosis</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Hydro-jetting equipment for thorough cleaning</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Trenchless repair capabilities</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                                              </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-white/20">
                  <h3 className="text-xl font-bold mb-4 text-orange-400">What to Look for When Hiring</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-blue-100">
                    <div className="text-center">
                      <Award className="w-12 h-12 text-yellow-400 mx-auto mb-2" />
                      <p className="font-semibold">Full Colorado & Castle Rock Licensing</p>
                    </div>
                    <div className="text-center">
                      <Shield className="w-12 h-12 text-green-400 mx-auto mb-2" />
                      <p className="font-semibold">Transparent Upfront Pricing</p>
                    </div>
                    <div className="text-center">
                      <Users className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                      <p className="font-semibold">Positive Reviews & BBB Rating</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section - Dark Gradient */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Ready for Honest, Professional Plumbing Service in Castle Rock?
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Join thousands of satisfied customers who chose transparent pricing over sales tactics
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-4xl font-black text-yellow-400 mb-2">$2,000</div>
                    <p className="text-blue-100">40 Gallon Water Heater<br />Fully Installed</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-black text-green-400 mb-2">$175</div>
                    <p className="text-blue-100">Sewer Camera Inspection<br />With Video Report</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-black text-orange-400 mb-2">$5,000</div>
                    <p className="text-blue-100">Sewer Spot Repair<br />In Your Yard</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  
                  <Link 
                    to="/pricing" 
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                  >
                    💰 View All Pricing
                  </Link>
                </div>
                <p className="text-sm text-blue-200 mt-6">
                  Serving Castle Rock since 2018 • Licensed Master Plumber • BBB A+ Rating • 4.8★ Reviews
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CastleRockPlumber;